/**
 * Organize afterpass — soft layer/column formation.
 *
 * The stress solve produces a faithful but ORGANIC shape: positions are
 * right in aggregate, yet nothing shares a baseline and near-layers stay
 * ragged. This pass firms that shape up without imposing strict rank
 * layers:
 *
 *   1. Column candidates — 1-D clusters of the CURRENT x-centers (the
 *      stress macro-structure decides who is near whom), each cluster then
 *      split by LOCAL ranks over the edges among its own members: a parent
 *      sitting inside its children's x-band separates into its own column,
 *      while a genuine layer (no internal edges) passes through whole.
 *   2. Column snap — columns order by mean x, near-identical neighbors
 *      merge, consecutive columns get geometric clearance (so the terminal
 *      separation pass never scatters a snapped member), and each member
 *      takes its column's x exactly — unless that would move it more than
 *      COLUMN_SNAP_MAX (the stress solve put it between layers on
 *      purpose; leave the rule-breaker organic).
 *   3. Barycenter stacking — snapped members order by the mean center-y of
 *      their neighbors outside the column, then stack with even
 *      ORGANIZE_GAP_Y spacing, centered on the mean of those barycenters.
 *      A parent with a fan of children ends up vertically centered on the
 *      fan; parallel chains straighten into rows.
 *
 * The pass is SELF-VALIDATING: it snapshots the component, measures
 * edge-length energy + chord crossings before and after, and reverts the
 * whole component when organization made the picture worse (rings and
 * dense meshes are circles, not ladders — geometry decides, not topology
 * heuristics).
 *
 * Columns containing a pinned node only get the x-snap for their free
 * members. Runs per component, in flow space, on every NON-DRAG solve:
 * re-running on warm solves is what keeps the crystallized shape stable
 * across edits (relax → re-crystallize lands on the same columns).
 */

import type { Rect } from "./stressLayout.js";
import { intraSccEdgeKeys, sccComponents, meanRadius } from "./graphDistances.js";
import { separationPasses } from "./separation.js";
import {
  ORGANIZE_MAX_SWEEPS,
  COLUMN_SPLIT_GAP,
  COLUMN_SNAP_MAX,
  COLUMN_SNAP_SIBLING_MAX,
  SATELLITE_DOCK_MAX,
  SATELLITE_DOCK_DOMINANCE,
  COLUMN_MIN_GAP,
  COLUMN_MERGE_BELOW,
  ORGANIZE_GAP_Y,
  SEP_GAP,
  SEP_PER_CROWD,
  SEP_CROWD_MAX,
  CROWD_BASE,
  CROWD_DEG_BASE,
  DENSITY_IDEAL_PER_CROWD,
  DENSITY_IDEAL_MAX,
} from "./layoutParams.js";

export interface OrganizeEdge {
  readonly from: string;
  readonly to: string;
  /** Ideal silhouette gap — same value the springs use. */
  readonly idealLen: number;
}

export interface OrganizeArgs {
  /** Component node ids (any order; sorted internally). */
  readonly ids: readonly string[];
  readonly pos: Map<string, { x: number; y: number }>;
  readonly size: ReadonlyMap<string, { w: number; h: number }>;
  readonly pinned: ReadonlyMap<string, Rect>;
  readonly edges: readonly OrganizeEdge[];
  /** Neighborhood jurisdiction (P2 flag): when present, column
   *  candidates never span two neighborhoods — alignment across a
   *  boundary is the picket-fence artifact. Acceptance gauges stay
   *  component-scoped (a scoped snap can still be vetoed for the
   *  crossings it creates against cross-boundary edges). */
  readonly hoodOf?: ReadonlyMap<string, string>;
}

/** Acceptance thresholds: reject when crossings grow beyond this slack…
 *  (Calibrated for the COARSE graph organize sees: motif assemblies shrink
 *  a CC to fewer, wider boxes — chord crossings between boxes inflate and
 *  each aligned box moves the share more.) */
const ACCEPT_CROSS_SLACK = 0.4;
const ACCEPT_CROSS_MIN_SLACK = 2;
/** …or the edge-length energy paid isn't covered by alignment gained.
 *  Energy = mean squared relative edge-length deviation. Inflation below
 *  ENERGY_FREE is accepted outright (a stress-settled layout has near-zero
 *  energy, so tiny worthwhile nudges must not need justification); beyond
 *  that the pass must BUY the energy with a real column-share gain, up to
 *  the hard ENERGY_MAX ceiling (wreckage no alignment can justify). */
const ACCEPT_ENERGY_FREE = 0.03;
const ACCEPT_ENERGY_MAX = 0.35;
const ACCEPT_ALIGN_GAIN = 0.18;
/** Per-node cap on a stack move; a column needing more stays organic. */
const STACK_MOVE_MAX = 280;
/** Cap on the rigid whole-stack barycenter shift. */
const STACK_SHIFT_MAX = 160;

/**
 * Decide the formation for one component: run the full sweep/acceptance
 * machinery on a TRIAL COPY of the positions and return the accepted
 * target map — or null when organization would make the picture worse.
 * The caller enforces accepted targets through standing anchor FORCES
 * (never placement), so the final layout is an equilibrium of the live
 * force system and interaction pulls against the formation.
 */
export function organizeDecide(
  args: OrganizeArgs,
): Map<string, { x: number; y: number }> | null {
  const { size, pinned } = args;
  const ids = [...args.ids].sort();
  if (ids.length < 3) return null;

  const realEdges = args.edges.filter((e) => e.from !== e.to);
  if (realEdges.length === 0) return null;

  // Trial copy — the decision must not touch the caller's state.
  const pos = new Map<string, { x: number; y: number }>();
  for (const [id, p] of args.pos) pos.set(id, { x: p.x, y: p.y });

  const cxOf = (id: string) => pos.get(id)!.x + size.get(id)!.w / 2;
  const cyOf = (id: string) => pos.get(id)!.y + size.get(id)!.h / 2;

  // ---- Quality gauges for the acceptance check --------------------------
  // Edge ideals arrive already density-stretched (the solver bakes the
  // topological crowd pad into every edge's idealLen) — the gauge and the
  // physics share one measuring stick by construction.
  const edgeEnergy = (): number => {
    let e = 0;
    for (const ef of realEdges) {
      const ideal =
        ef.idealLen +
        meanRadius(size.get(ef.from)!) +
        meanRadius(size.get(ef.to)!);
      const len = Math.hypot(cxOf(ef.to) - cxOf(ef.from), cyOf(ef.to) - cyOf(ef.from));
      const rel = (len - ideal) / ideal;
      e += rel * rel;
    }
    return e;
  };
  const chordCrossings = (): number => {
    const chords = realEdges.map((e) => ({
      f: e.from,
      t: e.to,
      ax: cxOf(e.from),
      ay: cyOf(e.from),
      bx: cxOf(e.to),
      by: cyOf(e.to),
    }));
    let count = 0;
    for (let i = 0; i < chords.length; i++) {
      for (let j = i + 1; j < chords.length; j++) {
        const a = chords[i]!;
        const b = chords[j]!;
        if (a.f === b.f || a.f === b.t || a.t === b.f || a.t === b.t) continue;
        const d1 = (a.bx - a.ax) * (b.ay - a.ay) - (a.by - a.ay) * (b.ax - a.ax);
        const d2 = (a.bx - a.ax) * (b.by - a.ay) - (a.by - a.ay) * (b.bx - a.ax);
        const d3 = (b.bx - b.ax) * (a.ay - b.ay) - (b.by - b.ay) * (a.ax - b.ax);
        const d4 = (b.bx - b.ax) * (a.by - b.ay) - (b.by - b.ay) * (a.bx - b.ax);
        if (d1 * d2 < 0 && d3 * d4 < 0) count++;
      }
    }
    return count;
  };

  /** Local column-share gauge (fraction of nodes whose center-x lies
   *  within 6px of another's) — measures what this pass exists to buy. */
  const columnShare = (): number => {
    const centers = ids.map((id) => cxOf(id)).sort((a, b) => a - b);
    let shared = 0;
    for (let i = 0; i < centers.length; i++) {
      if (
        (i > 0 && centers[i]! - centers[i - 1]! <= 6) ||
        (i < centers.length - 1 && centers[i + 1]! - centers[i]! <= 6)
      ) {
        shared++;
      }
    }
    return shared / centers.length;
  };

  // Density census — THE RULE: the more arrows, the more space. Crowd =
  // mesh-ness (edges among a node's neighbors); position-independent, so
  // re-solves land on the same pads. Crowded regions get stretched stack
  // gaps and column clearances below; chains/fans/gateways score ~0 and
  // keep the tight defaults.
  const adjacency = new Map<string, Set<string>>();
  for (const id of ids) adjacency.set(id, new Set());
  for (const e of realEdges) {
    adjacency.get(e.from)?.add(e.to);
    adjacency.get(e.to)?.add(e.from);
  }
  const crowd = new Map<string, number>();
  for (const id of ids) {
    // Mesh-ness: edges among this node's neighbors — arrows that compete
    // for the same area. Chains/fans/gateways score ~0.
    const nbs = [...adjacency.get(id)!];
    let m = 0;
    for (let i = 0; i < nbs.length; i++) {
      for (let j = i + 1; j < nbs.length; j++) {
        if (adjacency.get(nbs[i]!)!.has(nbs[j]!)) m++;
      }
    }
    // Hubs are dense in arrows: the node's own arrow load counts too.
    m += Math.max(0, nbs.length - CROWD_DEG_BASE);
    crowd.set(id, m);
  }
  const densityPad = (memberIds: readonly string[]): number => {
    // MAX, not mean — mirrors the solver's densityPadOf: one crowded
    // member is what makes the whole group need clearance.
    let worst = 0;
    for (const id of memberIds) worst = Math.max(worst, crowd.get(id) ?? 0);
    return Math.min(
      DENSITY_IDEAL_MAX,
      DENSITY_IDEAL_PER_CROWD * Math.max(0, worst - CROWD_BASE),
    );
  };

  const before = new Map<string, { x: number; y: number }>();
  for (const id of ids) {
    const p = pos.get(id)!;
    before.set(id, { x: p.x, y: p.y });
  }
  const energyBefore = edgeEnergy();
  const crossBefore = chordCrossings();
  const shareBefore = columnShare();

  const orderExempt = intraSccEdgeKeys(ids, realEdges);
  // Members of a cycle of three or more stay OUT of column crystallization
  // entirely: a ring is a circle, not a ladder (this pass's own charter).
  // Their local ranks are all equal, so cluster-splitting would fuse the
  // whole cycle into ONE column and the snap would crush its ring geometry
  // into a stack — trading real edge energy for a hollow alignment gain
  // the acceptance check tolerates. They still participate in energy and
  // barycenter measurement as fixed neighbors. Mutual PAIRS (2-cycles)
  // keep crystallizing: equal rank puts both in one column — the stacked
  // pair is exactly the shape a two-way edge wants.
  const compOf = sccComponents(ids, realEdges);
  const compSize = new Map<number, number>();
  for (const c of compOf.values()) compSize.set(c, (compSize.get(c) ?? 0) + 1);
  const ringExcluded = new Set<string>();
  for (const id of ids) {
    const c = compOf.get(id);
    if (c !== undefined && (compSize.get(c) ?? 0) >= 3) ringExcluded.add(id);
  }
  const neighbors = new Map<string, string[]>();
  for (const id of ids) neighbors.set(id, []);
  for (const e of realEdges) {
    neighbors.get(e.from)?.push(e.to);
    neighbors.get(e.to)?.push(e.from);
  }

  /** Longest-path ranks over the order-carrying (non-intra-SCC) edges
   *  among `members` — SCC members share a rank. */
  const localRanks = (members: readonly string[]): Map<string, number> => {
    const memberSet = new Set(members);
    const local = realEdges.filter(
      (e) =>
        memberSet.has(e.from) &&
        memberSet.has(e.to) &&
        !orderExempt.has(`${e.from}|${e.to}`),
    );
    const rank = new Map<string, number>();
    for (const id of members) rank.set(id, 0);
    for (let round = 0; round < members.length; round++) {
      let changed = false;
      for (const e of local) {
        const target = rank.get(e.from)! + 1;
        if (rank.get(e.to)! < target) {
          rank.set(e.to, target);
          changed = true;
        }
      }
      if (!changed) break;
    }
    return rank;
  };

  // Sweep to CONVERGENCE, not a fixed count: organize's output must be a
  // fixed point of its own sweep, or a maintenance re-solve (which feeds
  // organize its own previous output) derives different clusters and the
  // standing layout shifts. Snapshot positions per sweep; stop when a
  // sweep no longer moves anything materially.
  for (let sweep = 0; sweep < ORGANIZE_MAX_SWEEPS; sweep++) {
    const sweepBefore = new Map<string, { x: number; y: number }>();
    for (const id of ids) {
      const p = pos.get(id)!;
      sweepBefore.set(id, { x: p.x, y: p.y });
    }
    // 1a. X-clusters of current centers (ring members stay organic;
    // clusters never span a neighborhood boundary — R4).
    const hoodKey = (id: string) => args.hoodOf?.get(id) ?? "";
    const byX = ids
      .filter((id) => !ringExcluded.has(id))
      .sort(
        (a, b) =>
          (hoodKey(a) < hoodKey(b) ? -1 : hoodKey(a) > hoodKey(b) ? 1 : 0) ||
          cxOf(a) - cxOf(b) ||
          (a < b ? -1 : 1),
      );
    const clusters: string[][] = [];
    let cur: string[] = [];
    let prevCx = -Infinity;
    let prevHood = "";
    for (const id of byX) {
      const cx = cxOf(id);
      if (
        cur.length > 0 &&
        (cx - prevCx > COLUMN_SPLIT_GAP || hoodKey(id) !== prevHood)
      ) {
        clusters.push(cur);
        cur = [];
      }
      cur.push(id);
      prevCx = cx;
      prevHood = hoodKey(id);
    }
    if (cur.length > 0) clusters.push(cur);

    // 1b. Split each cluster by local ranks (edges among its members).
    // Members of one rank group crystallize together only when RELATED
    // (an edge between them, or a shared peer): satellites of different
    // hubs that merely landed at similar x would otherwise snap into a
    // ruler-straight picket fence of strangers (C2 — the "5 things
    // lined up" artifact; alignment must be EARNED by relatedness, not
    // imposed by proximity).
    const related = (a: string, b: string): boolean => {
      const na = neighbors.get(a)!;
      if (na.includes(b)) return true;
      const nb = new Set(neighbors.get(b)!);
      return na.some((p) => nb.has(p));
    };
    const subColumns: string[][] = [];
    for (const cluster of clusters) {
      const rank = localRanks(cluster);
      const byRank = new Map<number, string[]>();
      for (const id of cluster) {
        const r = rank.get(id)!;
        const arr = byRank.get(r) ?? [];
        arr.push(id);
        byRank.set(r, arr);
      }
      for (const r of [...byRank.keys()].sort((a, b) => a - b)) {
        const group = byRank.get(r)!.sort();
        if (group.length < 3) {
          subColumns.push(group);
          continue;
        }
        // Greedy related-components within the rank group: members
        // sharing an edge or a peer crystallize together; strangers
        // fall out as singleton columns instead of forming a rail.
        const left = new Set(group);
        while (left.size > 0) {
          const seedId = [...left].sort()[0]!;
          left.delete(seedId);
          const comp = [seedId];
          let grew = true;
          while (grew) {
            grew = false;
            for (const cand of [...left].sort()) {
              if (comp.some((m) => related(m, cand))) {
                comp.push(cand);
                left.delete(cand);
                grew = true;
              }
            }
          }
          subColumns.push(comp.sort());
        }
      }
    }

    const meanOf = (col: readonly string[]): number => {
      let m = 0;
      for (const id of col) m += cxOf(id);
      return m / col.length;
    };

    // 2a. Order by mean x, merge near-identical neighbors — never across
    // a neighborhood boundary (each cluster is single-hood by 1a, so the
    // first member's hood speaks for the group).
    subColumns.sort((a, b) => meanOf(a) - meanOf(b) || (a[0]! < b[0]! ? -1 : 1));
    const columns: string[][] = [];
    for (const group of subColumns) {
      const prev = columns[columns.length - 1];
      if (
        prev &&
        Math.abs(meanOf(group) - meanOf(prev)) < COLUMN_MERGE_BELOW &&
        hoodKey(prev[0]!) === hoodKey(group[0]!)
      ) {
        columns[columns.length - 1] = [...prev, ...group].sort();
      } else {
        columns.push(group);
      }
    }

    // 2b. Column x targets with geometric clearance.
    const colX: number[] = [];
    const colHalfW: number[] = [];
    for (let k = 0; k < columns.length; k++) {
      const col = columns[k]!;
      let halfW = 0;
      for (const id of col) halfW = Math.max(halfW, size.get(id)!.w / 2);
      const mean = meanOf(col);
      colHalfW.push(halfW);
      colX.push(
        k === 0
          ? mean
          : Math.max(
              mean,
              colX[k - 1]! +
                colHalfW[k - 1]! +
                halfW +
                COLUMN_MIN_GAP +
                (k > 0 ? densityPad([...columns[k - 1]!, ...col]) : 0),
            ),
      );
    }

    // 2c/3. Snap + barycenter stacking per column.
    for (let k = 0; k < columns.length; k++) {
      const col = columns[k]!;
      const colSet = new Set(col);
      const mean = colX[k]!;
      // Physics first: a column whose members have NO direct edges among
      // them (sibling satellites of shared hubs) may only snap what the
      // stress solve already nearly aligned — the full COLUMN_SNAP_MAX
      // license let structure drag strangers 240px onto one ruler line
      // (the 6-card rail). Members with real edges among them earn the
      // full snap (parent-child stacks, mutual pairs).
      const hasDirectEdge = realEdges.some(
        (e) => colSet.has(e.from) && colSet.has(e.to),
      );
      const snapCap = hasDirectEdge ? COLUMN_SNAP_MAX : COLUMN_SNAP_SIBLING_MAX;
      const snapped: string[] = [];
      for (const id of col) {
        if (Math.abs(cxOf(id) - mean) > snapCap) continue;
        if (!pinned.has(id)) pos.get(id)!.x = mean - size.get(id)!.w / 2;
        snapped.push(id);
      }

      if (snapped.length === 0 || snapped.some((id) => pinned.has(id))) continue;

      // Gentle vertical crystallization: order by each member's
      // barycenter (mean center-y of its outside neighbors — the classic
      // crossing minimizer), even out the gaps, and rigidly nudge the
      // whole stack toward the column's mean barycenter. Every per-node
      // move is capped; a column too disordered to crystallize within
      // the cap stays organic — so a bary order that disagrees wildly
      // with the stress order simply doesn't apply.
      const orderBary = new Map<string, number>();
      for (const id of snapped) {
        let s = 0;
        let n = 0;
        for (const peer of neighbors.get(id)!) {
          if (colSet.has(peer)) continue;
          s += cyOf(peer);
          n++;
        }
        orderBary.set(id, n > 0 ? s / n : cyOf(id));
      }
      const order = [...snapped].sort(
        (a, b) => orderBary.get(a)! - orderBary.get(b)! || (a < b ? -1 : 1),
      );
      const stackGap = ORGANIZE_GAP_Y + densityPad(order);
      let total = 0;
      let massCy = 0;
      for (const id of order) {
        total += size.get(id)!.h;
        massCy += cyOf(id);
      }
      total += stackGap * (order.length - 1);
      massCy /= order.length;

      let barySum = 0;
      let baryN = 0;
      for (const id of order) {
        for (const peer of neighbors.get(id)!) {
          if (colSet.has(peer)) continue;
          barySum += cyOf(peer);
          baryN++;
        }
      }
      const meanBary = baryN > 0 ? barySum / baryN : massCy;
      const shift = Math.max(
        -STACK_SHIFT_MAX,
        Math.min(STACK_SHIFT_MAX, meanBary - massCy),
      );

      const targets: number[] = [];
      let y = massCy + shift - total / 2;
      let feasible = true;
      for (const id of order) {
        targets.push(y);
        if (Math.abs(y - pos.get(id)!.y) > STACK_MOVE_MAX) feasible = false;
        y += size.get(id)!.h + stackGap;
      }
      if (!feasible) continue;
      order.forEach((id, i) => {
        pos.get(id)!.y = targets[i]!;
      });
    }

    // Resolve the collisions this sweep created BEFORE the convergence
    // check: separation is part of the fixed point. If it ran after the
    // loop, organize's output would be "converged sweeps + displacement",
    // which the NEXT (maintenance) solve re-clusters differently — the
    // standing layout would shift on every recompute.
    const sepGen = separationPasses({
      ids,
      pos,
      size,
      pinned,
      gap: SEP_GAP,
      maxPasses: 30,
      padOf: (id) =>
        Math.min(
          SEP_CROWD_MAX,
          SEP_PER_CROWD * Math.max(0, (crowd.get(id) ?? 0) - CROWD_BASE),
        ),
    });
    let sepStep = sepGen.next();
    while (!sepStep.done) sepStep = sepGen.next();

    let maxMove = 0;
    for (const id of ids) {
      const b = sweepBefore.get(id)!;
      const p = pos.get(id)!;
      maxMove = Math.max(maxMove, Math.abs(p.x - b.x), Math.abs(p.y - b.y));
    }
    if (maxMove < 1) break;
  }

  // 4. Satellite dock — AFTER the sweeps converge (docking inside the
  // loop perturbs the x-clusters each sweep and muddles crystallization
  // on small graphs): a degree-1 node aligns to its ONLY peer's axis. A
  // lone full-size satellite stays out of formations (pairs are for
  // satellite-SIZED leaves) and lands wherever the forces left it — a
  // single-connection card sitting 80px off its peer's column reads as
  // an accident (the era-R3 reference nitpick: Orders Demo vs Ops).
  // Dock only on a CLEARLY dominant axis (near the diagonal the "right"
  // axis is a coin flip, and a chain end nudged into its peer's column
  // breaks the chain's row reading), capped, never pinned or ring
  // members, never across hoods (R4). Idempotent — a docked node re-docks
  // to zero on the next maintenance pass, so the fixed point holds. The
  // trailing separation resolves overlaps and the acceptance gauges veto
  // the whole result if docking cost crossings or energy.
  /** Would `id` at (x, y) collide with any OTHER node (SEP_GAP pad)?
   *  A dock/row-snap into an occupied spot just fights the separation
   *  pass — it would shove the node back and churn its whole row — so an
   *  occupied landing is skipped, not attempted. */
  const collides = (id: string, x: number, y: number): boolean => {
    const s = size.get(id)!;
    for (const other of ids) {
      if (other === id) continue;
      const op = pos.get(other)!;
      const os = size.get(other)!;
      if (
        x < op.x + os.w + SEP_GAP &&
        x + s.w + SEP_GAP > op.x &&
        y < op.y + os.h + SEP_GAP &&
        y + s.h + SEP_GAP > op.y
      ) {
        return true;
      }
    }
    return false;
  };

  {
    const hoodKey = (id: string) => args.hoodOf?.get(id) ?? "";
    for (const id of ids) {
      if (pinned.has(id) || ringExcluded.has(id)) continue;
      const nbs = neighbors.get(id)!;
      if (nbs.length !== 1) continue;
      const peer = nbs[0]!;
      if (!pos.has(peer)) continue;
      if (hoodKey(id) !== hoodKey(peer)) continue;
      const dx = cxOf(peer) - cxOf(id);
      const dy = cyOf(peer) - cyOf(id);
      const p = pos.get(id)!;
      if (Math.abs(dx) <= Math.abs(dy) * SATELLITE_DOCK_DOMINANCE) {
        if (
          Math.abs(dx) > 0 &&
          Math.abs(dx) <= SATELLITE_DOCK_MAX &&
          !collides(id, p.x + dx, p.y)
        ) {
          p.x += dx;
        }
      } else if (
        Math.abs(dy) <= Math.abs(dx) * SATELLITE_DOCK_DOMINANCE &&
        Math.abs(dy) > 0 &&
        Math.abs(dy) <= SATELLITE_DOCK_MAX &&
        !collides(id, p.x, p.y + dy)
      ) {
        p.y += dy;
      }
    }
  }

  // 5. Chain row-snap — physics-first straightening. A linear chain
  // (head → m₁ → … → mₖ → tail whose INTERIOR members each have exactly
  // one in- and one out-edge) reads forward but its interior drifts off
  // the row the terminal members settled on (dedicated terminal pairs
  // ride a rigid motif; the rest float). Snap every free member onto the
  // chain's MEDIAN center-y — the order and each member's column x come
  // from where physics put them; this only formalizes the row. Capped per
  // node, skipped when the landing is occupied, and the component
  // acceptance gauge below vetoes the whole result if straightening cost
  // real energy or crossings (a chain the stress solve bent on purpose,
  // e.g. one wrapped around a hub, stays organic).
  {
    const dout = new Map<string, Set<string>>();
    const din = new Map<string, Set<string>>();
    for (const id of ids) {
      dout.set(id, new Set());
      din.set(id, new Set());
    }
    for (const e of realEdges) {
      dout.get(e.from)!.add(e.to);
      din.get(e.to)!.add(e.from);
    }
    const only = (s: Set<string>) => [...s][0]!;
    // An interior chain member: exactly one in- and one out-edge, and not
    // a ring member (a ring is a circle, not a row — organize's charter).
    const interior = (id: string) =>
      !ringExcluded.has(id) &&
      din.get(id)!.size === 1 &&
      dout.get(id)!.size === 1;
    const seen = new Set<string>();
    for (const seed of [...ids].sort()) {
      if (!interior(seed) || seen.has(seed)) continue;
      // Walk left to the run's first interior member (guarded against a
      // 2-cycle of interiors, which is not ring-excluded).
      let start = seed;
      const walked = new Set([start]);
      while (interior(only(din.get(start)!)) && !walked.has(only(din.get(start)!))) {
        start = only(din.get(start)!);
        walked.add(start);
      }
      // Collect the interior run, then bracket with its head and tail.
      const run: string[] = [];
      let cur = start;
      while (interior(cur) && !run.includes(cur)) {
        run.push(cur);
        seen.add(cur);
        cur = only(dout.get(cur)!);
      }
      const head = only(din.get(start)!);
      const tail = cur;
      const chain = [head, ...run, tail];
      // A cycle (head or tail folded back into the run) is not a chain.
      if (run.includes(head) || run.includes(tail)) continue;
      // Median row target from where physics settled the members — the
      // anchored majority pulls the drifter in, not the reverse.
      const cys = chain.map(cyOf).sort((a, b) => a - b);
      const target = cys[Math.floor((cys.length - 1) / 2)]!;
      for (const id of chain) {
        if (pinned.has(id)) continue;
        const want = target - size.get(id)!.h / 2;
        if (Math.abs(want - pos.get(id)!.y) > STACK_MOVE_MAX) continue;
        if (collides(id, pos.get(id)!.x, want)) continue;
        pos.get(id)!.y = want;
      }
    }
  }

  // ---- Acceptance: geometry judges the result ---------------------------
  const crossAfter = chordCrossings();
  const energyAfter = edgeEnergy();
  const shareAfter = columnShare();
  const crossBudget =
    crossBefore + Math.max(ACCEPT_CROSS_MIN_SLACK, crossBefore * ACCEPT_CROSS_SLACK);
  const meanBefore = energyBefore / realEdges.length;
  const meanAfter = energyAfter / realEdges.length;
  const inflation = meanAfter - meanBefore;
  const energyOk =
    inflation <= ACCEPT_ENERGY_FREE ||
    (inflation <= ACCEPT_ENERGY_MAX && shareAfter - shareBefore >= ACCEPT_ALIGN_GAIN);
  const reject = crossAfter > crossBudget || !energyOk;
  if (
    // eslint-disable-next-line no-undef -- browser builds have no `process`
    (typeof process !== "undefined" && process.env?.ARCHLANG_ORGANIZE_DEBUG) ||
    (globalThis as { __ARCH_ORG_DEBUG?: boolean }).__ARCH_ORG_DEBUG
  ) {
    // eslint-disable-next-line no-console
    console.log(
      `[organize] n=${ids.length} cross ${crossBefore}→${crossAfter} (budget ${crossBudget.toFixed(1)}) ` +
        `meanE ${meanBefore.toFixed(4)}→${meanAfter.toFixed(4)} share ${shareBefore.toFixed(2)}→${shareAfter.toFixed(2)} ` +
        `${reject ? "REVERT" : "accept"}`,
    );
  }
  return reject ? null : pos;
}
